import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Settings2, Copy, Check } from "lucide-react";
import { useDash } from "./ctx";
import { MailList } from "./mail/MailList";
import { MailFlagged } from "./mail/MailFlagged";
import { MailDetail } from "./mail/MailDetail";
import { MailSettingsSheet } from "./mail/MailSettingsSheet";
import { ActionWizard } from "./mail/ActionWizard";
import { V } from "@/lib/vocab";

const isDesktop = () => typeof window !== "undefined" && window.innerWidth >= 1024;
const ACTION_MSG = { archive: "Archived", trash: "Moved to Trash", restore: "Back in your Inbox", dismiss: "Dismissed", read: "Marked as read", unread: "Marked as unread", snooze: "Snoozed" };
const NUDGE_KEY = "askredline.mail.cta_seen";
const nudgeCount = () => Number(localStorage.getItem(NUDGE_KEY) || 0);

// Two places only: Inbox (everything not archived or deleted — published mail stays, tagged, like Outlook)
// and Done (archived / deleted / junk). Opening an email marks it read; Archive / Delete live on the toolbar and on row hover.
export default function Mail() {
  const { tenantId, role, tenant } = useDash();
  // Modules this community has on — Mail never offers a destination the community doesn't have.
  const modules = { requests: tenant?.features?.requests !== false && (tenant?.mode || "answers_requests") !== "answers_only", events: tenant?.features?.events !== false };
  const [params, setParams] = useSearchParams();
  const [box, setBox] = useState(params.get("box") === "done" ? "done" : "inbox");
  const [outcome, setOutcome] = useState("");
  const [kind, setKind] = useState(params.get("kind") === "decisions" || params.get("kind") === "email" ? params.get("kind") : "");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  // Copilot Inbox: when ON the list is email + decision rows from every module (inbox_on rides on the counts).
  const inboxOn = counts.inbox_on === true;
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState(null);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(params.get("item"));
  const [selection, setSelection] = useState(() => new Set());
  const [mobileDetail, setMobileDetail] = useState(!!params.get("item"));
  const [showSettings, setShowSettings] = useState(params.get("settings") === "1");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [version, setVersion] = useState(0);
  const [wizard, setWizard] = useState({ open: false, item: null });
  const [nudge, setNudge] = useState(() => { const n = nudgeCount(); localStorage.setItem(NUDGE_KEY, String(n + 1)); return n < 3; });
  const endNudge = () => { setNudge(false); localStorage.setItem(NUDGE_KEY, "3"); };
  const pendingNext = useRef(null);
  const anchorId = useRef(null);
  const [explicitId, setExplicitId] = useState(null);
  // A link from a report / notification email points at specific emails. They've often moved since
  // (archived, trashed, purged), so resolve them across every box instead of silently showing
  // "Inbox zero" or the wrong row (iter516).
  const [recon, setRecon] = useState([]);
  const healed = useRef(false);
  const linkIds = useMemo(() => {
    const ids = (params.get("flagged") || "").split(",").map((s) => s.trim()).filter(Boolean);
    const one = params.get("item");
    if (one && !ids.includes(one)) ids.push(one);
    return ids.slice(0, 12);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!tenantId || !linkIds.length) return;
    api.get(`/tenants/${tenantId}/mail/reconcile?ids=${linkIds.join(",")}`)
      .then((r) => setRecon(r.data.items || [])).catch(() => {});
  }, [tenantId, linkIds]);
  const openFlagged = useCallback((r) => {
    setBox(r.box === "done" ? "done" : "inbox");
    setSelectedId(r.id); setExplicitId(r.id); setMobileDetail(true);
  }, []);
  useEffect(() => {
    // Self-heal the link: open the one email it meant, wherever it ended up.
    if (healed.current || !recon.length) return;
    const moved = recon.filter((r) => !r.waiting && r.box !== "gone");
    const target = moved.find((r) => r.id === params.get("item")) || (moved.length === 1 ? moved[0] : null);
    if (!target) return;
    healed.current = true;
    setBox(target.box === "done" ? "done" : "inbox");
    setSelectedId(target.id); setExplicitId(target.id);
  }, [recon]); // eslint-disable-line react-hooks/exhaustive-deps
  // A board seat acts on Mail only when the community's dial says "Can act" AND this person holds
  // the Configure grant — the backend decides (`can_act`); until settings load, board = read-only.
  const canAct = settings ? settings.can_act !== false : role !== "board_member";

  const load = useCallback(() => {
    if (!tenantId) return;
    const qs = new URLSearchParams({ status: box });
    if (q.trim()) qs.set("q", q.trim());
    if (box === "done" && outcome) qs.set("outcome", outcome);
    if (box === "inbox" && kind) qs.set("kind", kind);
    api.get(`/tenants/${tenantId}/mail/items?${qs}`)
      .then((r) => {
        const list = r.data.items || [];
        setItems(list); setCounts(r.data.counts || {});
        const p = pendingNext.current;
        if (p && !list.some((i) => i.id === p.id)) { setSelectedId(p.next); if (!p.next) setMobileDetail(false); }
        pendingNext.current = null;
      })
      .catch((e) => { if (e?.response?.status !== 403) toast.error("Couldn't load Mail"); }).finally(() => setLoading(false));
  }, [tenantId, box, q, outcome, kind]);
  useEffect(() => { setLoading(true); const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { if (tenantId) api.get(`/tenants/${tenantId}/mail/settings`).then((r) => setSettings(r.data)).catch((e) => setSettingsError(e?.response?.status || true)); }, [tenantId]);

  const bump = () => { setVersion((v) => v + 1); load(); window.dispatchEvent(new CustomEvent("askredline:counts-changed")); };
  const selected = useMemo(() => items.find((i) => i.id === selectedId) || (isDesktop() ? items[0] : null) || null, [items, selectedId]);
  const switchBox = (k) => { setBox(k); setOutcome(""); setSelectedId(null); setExplicitId(null); anchorId.current = null; setSelection(new Set()); setMobileDetail(false); setParams(k === "done" ? { box: k } : {}, { replace: true }); };
  // Outlook selection: click opens it, Ctrl/Cmd+click adds it to the selection, Shift+click selects the range.
  const select = (it, e) => {
    if (e && (e.ctrlKey || e.metaKey)) { toggle(it.id); anchorId.current = it.id; return; }
    if (e && e.shiftKey && anchorId.current) {
      const a = items.findIndex((i) => i.id === anchorId.current), b = items.findIndex((i) => i.id === it.id);
      if (a >= 0 && b >= 0) { const [lo, hi] = a < b ? [a, b] : [b, a]; setSelection((s) => { const n = new Set(s); items.slice(lo, hi + 1).forEach((i) => n.add(i.id)); return n; }); return; }
    }
    anchorId.current = it.id;
    setSelectedId(it.id); setExplicitId(it.id); setMobileDetail(true);
  };
  // Local read-state patch — no refetch, the row just loses its bold and the badges tick down/up.
  const patchRead = (id, readAt) => {
    const isDecision = items.find((i) => i.id === id)?.source === "action";   // an open decision counts until it's handled, read or not
    setItems((list) => list.map((i) => (i.id === id ? { ...i, read_at: readAt } : i)));
    if (!isDecision) setCounts((c) => ({ ...c, unread: Math.max(0, (c.unread || 0) + (readAt ? -1 : 1)) }));
    window.dispatchEvent(new CustomEvent("askredline:counts-changed"));
  };
  const setRead = async (it, read) => {
    try { const r = await api.post(`/tenants/${tenantId}/mail/items/${it.id}/read`, { read }); patchRead(it.id, r.data.read_at); if (!read) toast.success("Marked as unread"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that"); }
  };
  const readAll = async () => {
    try { const r = await api.post(`/tenants/${tenantId}/mail/read-all`, {}); toast.success(`Marked ${r.data.count} email${r.data.count === 1 ? "" : "s"} as read`); bump(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that"); }
  };
  const emptyTrash = async () => {
    if (!window.confirm("Empty Trash? Every deleted email here is gone for good.")) return;
    try { const r = await api.post(`/tenants/${tenantId}/mail/trash/empty`, {}); toast.success(`Trash emptied — ${r.data.count} email${r.data.count === 1 ? "" : "s"} deleted for good`); setSelectedId(null); setSelection(new Set()); bump(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that"); }
  };
  const afterAction = (changedId) => {
    const idx = items.findIndex((i) => i.id === changedId);
    const nxt = items[idx + 1] || items[idx - 1] || null;
    pendingNext.current = { id: changedId, next: nxt ? nxt.id : null };
    bump();
  };
  const quick = async (action, it) => {
    try { await api.post(`/tenants/${tenantId}/mail/items/${it.id}/${action}`, {}); toast.success(ACTION_MSG[action]); afterAction(it.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that"); }
  };
  // Decision rows refresh on a short poll too — a teammate handling something in its module heals the row here.
  useEffect(() => {
    if (!inboxOn || box !== "inbox") return;
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [inboxOn, box, load]);
  const toggle = (id) => setSelection((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulk = async (action) => {
    const ids = [...selection];
    try {
      const r = await api.post(`/tenants/${tenantId}/mail/items/bulk`, { ids, action });
      toast.success(`${ACTION_MSG[action]} — ${r.data.count} email${r.data.count === 1 ? "" : "s"}`);
      setSelection(new Set());
      if (action === "read" || action === "unread") bump();
      else if (selected && ids.includes(selected.id)) afterAction(selected.id); else bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that"); }
  };
  const move = (dir) => {
    if (!items.length) return;
    const idx = Math.max(0, items.findIndex((i) => i.id === (selected?.id)));
    const nxt = items[Math.min(items.length - 1, Math.max(0, idx + dir))];
    if (nxt) { setSelectedId(nxt.id); setExplicitId(nxt.id); anchorId.current = nxt.id; }
  };
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable || document.querySelector("[role=dialog]")) return;
      if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); move(1); }
      else if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); move(-1); }
      else if (e.key === "Escape") { setMobileDetail(false); setSelection(new Set()); }
      else if (!canAct) return;
      else if (e.key === "Enter" && selected) { e.preventDefault(); window.dispatchEvent(new CustomEvent("askredline:mail-action", { detail: selected.id })); }
      else if ((e.key === "Delete" || e.key === "Backspace") && selected) { e.preventDefault(); window.dispatchEvent(new CustomEvent("askredline:mail-dismiss", { detail: selected.id })); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const copyAddress = () => {
    if (!settings?.address) return;
    navigator.clipboard?.writeText(settings.address).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  const canManage = ["hoa_manager", "regional_manager", "company_admin", "super_admin", "platform_admin"].includes(role);
  const hidden = settingsError === 403;

  if (hidden) {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center text-muted-foreground flex flex-col items-center gap-2" data-testid="mail-hidden">
        <Inbox className="h-8 w-8 text-ink/20" />
        <div className="font-semibold text-ink">Mail isn't shared with {V.grp()} members here</div>
        <div className="text-sm max-w-md">A manager can change that in Mail settings → “{V.Grp()} members in Mail”.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 -mb-6" data-testid="mail-page">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <PageTitle title={<><Inbox className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {V.mod("mail", "Mail")}</>} about={<>{inboxOn
              ? <span data-testid="mail-inbox-on-note">Every email forwarded here, every TownSq notice — and, with <span className="font-semibold text-ink">Copilot Inbox</span> on, every decision from every module as a row. Open one: <span className="font-semibold text-ink">Ask</span> for advice, <span className="font-semibold text-ink">Handle with Copilot</span> to draft and approve.</span>
              : canAct
              ? <>Emails forwarded to this {V.lc("org")}'s address, and every TownSq notice. Open one — the <span className="font-semibold text-ink">red button</span> says what should happen next, and you confirm before anything goes live.</>
              : <span data-testid="mail-readonly-note">Emails forwarded to this {V.lc("org")}'s address, and every TownSq notice. You can read everything here; publishing, archiving and deleting are up to the managers.</span>}</>} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {settings?.address ? (
            <button type="button" onClick={copyAddress} data-testid="mail-address-chip" title="Copy the address — forward anything here"
              className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-3.5 py-2 text-sm font-mono text-ink hover:border-ink transition-colors">
              {settings.address} {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-ink/50" />}
            </button>
          ) : settings && !settings.domain ? (
            <span data-testid="mail-address-missing" className="text-xs text-amber-700 font-medium">Mail address not configured (MAIL_INBOUND_DOMAIN)</span>
          ) : null}
          <button type="button" data-testid="mail-settings-button" onClick={() => setShowSettings(true)}
            className="h-10 w-10 rounded-full border border-sand-200 bg-white flex items-center justify-center hover:border-ink transition-colors" title="Mail settings">
            <Settings2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <MailFlagged rows={recon} onOpen={openFlagged} />

      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden lg:grid lg:grid-cols-[minmax(320px,420px)_1fr] lg:min-h-[calc(100vh-230px)]" data-testid="mail-client">
        <div className={`min-w-0 border-b lg:border-b-0 lg:border-r border-sand-200 ${mobileDetail ? "hidden lg:block" : "block"}`}>
          <MailList items={items} loading={loading} box={box} onBox={switchBox} counts={counts} outcome={outcome} onOutcome={setOutcome}
            selectedId={selected?.id} onSelect={select} address={settings?.address} q={q} onQ={setQ} canAct={canAct}
            inboxOn={inboxOn} kind={kind} onKind={(k) => { setKind(k); setSelectedId(null); setExplicitId(null); setSelection(new Set()); }}
            selection={selection} onToggle={toggle} onSelectAll={() => setSelection(new Set(items.map((i) => i.id)))} onClearSelection={() => setSelection(new Set())}
            onBulk={bulk} onQuick={quick} onRead={setRead} onReadAll={readAll} onEmptyTrash={emptyTrash} />
        </div>
        <div className={`min-w-0 ${mobileDetail ? "block" : "hidden lg:block"}`}>
          {selected ? (
            <MailDetail key={selected.id} tenantId={tenantId} itemId={selected.id} version={version} settings={settings} nudge={nudge && canAct} onNudged={endNudge} canAct={canAct}
              autoRead={canAct && explicitId === selected.id && !selected.read_at} readAt={selected.read_at} onRead={patchRead} modules={modules}
              onBack={() => setMobileDetail(false)} onChanged={afterAction} onTakeAction={(it) => setWizard({ open: true, item: it })} />
          ) : (
            <div data-testid="mail-detail-empty" className="hidden lg:flex h-full min-h-[300px] p-12 text-center text-muted-foreground items-center justify-center flex-col gap-2">
              <Inbox className="h-8 w-8 text-ink/20" />
              {items.length ? (inboxOn ? "Select an email or a decision to open it." : "Select an email to read it.") : box === "inbox" ? "Inbox zero — nothing waiting on you." : "Nothing handled yet."}
            </div>
          )}
        </div>
      </div>

      <ActionWizard open={wizard.open} onOpenChange={(o) => setWizard((w) => ({ ...w, open: o }))} tenantId={tenantId} item={wizard.item} modules={modules} tz={tenant?.timezone}
        settings={settings} canManage={canManage} onChanged={afterAction} onSettings={setSettings} />
      <MailSettingsSheet open={showSettings} onOpenChange={(o) => { setShowSettings(o); if (!o && params.get("settings")) setParams({}, { replace: true }); }}
        tenantId={tenantId} tenant={tenant} role={role} settings={settings} onSettings={setSettings} />
    </div>
  );
}
